import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";

interface Stakeholder {
  title: string;
  gif: string;
  description: string;
  demoUrl: string;
  status: "live" | "partial" | "in-progress";
}

const getStatusConfig = (status: Stakeholder["status"]) => {
  switch (status) {
    case "live":
      return {
        label: "Live",
        className: "bg-green-500/10 text-green-500 hover:bg-green-500/20",
      };
    case "partial":
      return {
        label: "Partial",
        className: "bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20",
      };
    case "in-progress":
      return {
        label: "In Progress",
        className: "bg-blue-500/10 text-blue-500 hover:bg-blue-500/20",
      };
  }
};

const GifModal = ({ isOpen, onClose, gif, title }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-4xl w-full mx-auto p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-semibold mb-4">{title}</h2>
        <div className="aspect-video w-full rounded-lg overflow-hidden">
          <img src={gif} alt={title} className="w-full h-full object-contain" />
        </div>
      </div>
    </div>
  );
};

export const StakeholderCarousels = () => {
  const [selectedGif, setSelectedGif] = useState(null);

  const stakeholderData1: Stakeholder[] = [
    {
      title: "Hoteliers",
      gif: "/supplyfied-upload/AIConsultant.gif",
      description:
        "AI-driven tools to optimize operations, procurement, and compliance.",
      demoUrl: "https://demo-slate.supplyfied.com/",
      status: "live",
    },
    {
      title: "Suppliers",
      gif: "/supplyfied-upload/vendorplat.gif",
      description:
        "Centralized platform for real-time bids, order management, and market growth.",
      demoUrl: "https://suppliers-uat.supplyfied.com/",
      status: "live",
    },
  ];
  const stakeholderData2: Stakeholder[] = [
    {
      title: "Regulators",
      gif: "/supplyfied-upload/Regulators.gif",
      description:
        "Automated compliance tools and real-time data for seamless regulation.",
      demoUrl: "https://demo-slate.supplyfied.com/",
      status: "partial",
    },
    {
      title: "Investors",
      gif: "/supplyfied-upload/investors.gif",
      description:
        "Real-time performance insights and ROI tracking with investment recommendations.",
      demoUrl: "https://demo-slate.supplyfied.com/",
      status: "in-progress",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20,
      },
    },
    hover: {
      y: -10,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
      },
    },
  };

  return (
    <div className="min-h-screen p-8 relative">
      <div className="absolute inset-4 border border-primary/10 rounded-3xl bg-gradient-to-b from-white via-muted/20 to-accent/30 shadow-[inset_0_0_20px_rgba(0,0,0,0.03)] backdrop-blur-[2px]">
        <div className="absolute inset-0 bg-grid-black/[0.02] rounded-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto py-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {stakeholderData1.map((item) => (
            <motion.div
              key={item.title}
              variants={cardVariants}
              whileHover="hover"
              className="flex flex-col gap-4 group"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-3xl font-semibold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  {item.title}
                </h3>
                <Badge
                  variant="secondary"
                  className={`${
                    getStatusConfig(item.status).className
                  } transition-all duration-300 shadow-sm`}
                >
                  {getStatusConfig(item.status).label}
                </Badge>
              </div>
              <Card className="overflow-hidden bg-white border-none shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-[1.02]">
                <CardContent className="p-0">
                  <div
                    className="w-full relative overflow-hidden"
                    style={{
                      aspectRatio: "1 / 1",
                    }}
                  >
                    <img
                      src={item.gif}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </CardContent>
              </Card>
              <div className="space-y-3">
                <p className="text-gray-600 text-lg leading-relaxed">
                  {item.description}
                </p>
                <Button
                  onClick={() => window.open(item.demoUrl, "_blank")}
                  className="w-full bg-gradient-to-r from-primary to-primary-dark hover:opacity-90 text-white gap-2 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  Try Demo <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16"
        >
          {stakeholderData2.map((item) => (
            <motion.div
              key={item.title}
              variants={cardVariants}
              whileHover="hover"
              className="flex flex-col gap-4 group"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-3xl font-semibold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  {item.title}
                </h3>
                <Badge
                  variant="secondary"
                  className={`${
                    getStatusConfig(item.status).className
                  } transition-all duration-300 shadow-sm`}
                >
                  {getStatusConfig(item.status).label}
                </Badge>
              </div>
              <Card className="overflow-hidden bg-white border-none shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-[1.02]">
                <CardContent className="p-0">
                  <div
                    className="w-full relative overflow-hidden"
                    style={{
                      aspectRatio: "1 / 1",
                    }}
                  >
                    <img
                      src={item.gif}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </CardContent>
              </Card>
              <div className="space-y-3">
                <p className="text-gray-600 text-lg leading-relaxed">
                  {item.description}
                </p>
                <Button
                  onClick={() =>
                    setSelectedGif({ gif: item.gif, title: item.title })
                  }
                  className="w-full bg-gradient-to-r from-primary to-primary-dark hover:opacity-90 text-white gap-2 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  See Demo <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
      <GifModal
        isOpen={!!selectedGif}
        onClose={() => setSelectedGif(null)}
        gif={selectedGif?.gif}
        title={selectedGif?.title}
      />
    </div>
  );
};
